// Floating toolbar with toggle buttons for visualization layers.
// Sits over the video (top-center) with glass-morphism styling.

import React from 'react';
import { Box, Divider, Tooltip } from '@mui/material';
import VideocamOutlinedIcon from '@mui/icons-material/VideocamOutlined';
import VideocamOffOutlinedIcon from '@mui/icons-material/VideocamOffOutlined';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import TimelineIcon from '@mui/icons-material/Timeline';
import ColorLensOutlinedIcon from '@mui/icons-material/ColorLensOutlined';
import LocalFireDepartmentIcon from '@mui/icons-material/LocalFireDepartment';
import ViewInArOutlinedIcon from '@mui/icons-material/ViewInArOutlined';
import ThreeDRotationOutlinedIcon from '@mui/icons-material/ThreeDRotationOutlined';
import ScreenRotationIcon from '@mui/icons-material/ScreenRotation';
import { VisionOverlayModes } from './VisionOverlay';
import { colors } from '../theme';

interface Props {
  modes: VisionOverlayModes;
  onChange: (modes: VisionOverlayModes) => void;
}

interface ToggleDef {
  key: keyof VisionOverlayModes;
  label: string;
  tooltip: string;
  icon: React.ReactNode;
  /** If true, this toggle means "on = hidden" — invert the active state. */
  inverted?: boolean;
}

const TOGGLES: ToggleDef[] = [
  {
    key: 'skeleton',
    label: 'Pose',
    tooltip: 'Show pose skeleton',
    icon: <PersonOutlineIcon sx={{ fontSize: 20 }} />,
  },
  {
    key: 'contour',
    label: 'Contour',
    tooltip: 'Show frontal-area silhouette',
    icon: <TimelineIcon sx={{ fontSize: 20 }} />,
  },
  {
    key: 'dragHeatmap',
    label: 'Heatmap',
    tooltip: 'Drag heatmap — red = highest watts per pixel',
    icon: <LocalFireDepartmentIcon sx={{ fontSize: 20 }} />,
  },
  {
    key: 'segmentation',
    label: 'Segments',
    tooltip: 'Color-code body parts',
    icon: <ColorLensOutlinedIcon sx={{ fontSize: 20 }} />,
  },
  {
    key: 'mesh',
    label: 'Mesh',
    tooltip: 'Show 3D body mesh',
    icon: <ViewInArOutlinedIcon sx={{ fontSize: 20 }} />,
  },
  {
    key: 'pose3d',
    label: '3D',
    tooltip: 'Show rotating 3D pose preview',
    icon: <ThreeDRotationOutlinedIcon sx={{ fontSize: 20 }} />,
  },
];

export default function VisualizationToolbar({ modes, onChange }: Props) {
  return (
    <Box
      sx={{
        position: 'absolute',
        top: 16,
        left: '50%',
        transform: 'translateX(-50%)',
        display: 'flex',
        gap: 0.5,
        p: 0.5,
        borderRadius: 999,
        backgroundColor: 'rgba(10, 14, 26, 0.75)',
        backdropFilter: 'blur(16px)',
        WebkitBackdropFilter: 'blur(16px)',
        border: `1px solid ${colors.border}`,
        boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
        zIndex: 10,
      }}
    >
      {TOGGLES.map((t) => {
        const active = modes[t.key];
        return (
          <Tooltip key={t.key} title={t.tooltip}>
            <Box
              onClick={() => onChange({ ...modes, [t.key]: !active })}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.75,
                px: 1.75,
                py: 1,
                borderRadius: 999,
                cursor: 'pointer',
                color: active ? colors.textInverse : colors.textMuted,
                background: active ? colors.gradBrand : 'transparent',
                transition: 'all 180ms',
                fontFamily: '"Inter", sans-serif',
                fontSize: '0.75rem',
                fontWeight: 600,
                letterSpacing: '0.02em',
                '&:hover': {
                  backgroundColor: active ? undefined : 'rgba(255,255,255,0.06)',
                  color: active ? colors.textInverse : colors.text,
                },
              }}
            >
              {t.icon}
              <Box component="span">{t.label}</Box>
            </Box>
          </Tooltip>
        );
      })}

      <Divider
        orientation="vertical"
        flexItem
        sx={{ mx: 0.5, my: 0.5, borderColor: colors.border }}
      />

      {/* Video toggle: shows the video feed behind overlays. When off, only
          the AI overlays are visible on a black background. */}
      <Tooltip
        title={modes.videoOff ? 'Show camera feed' : 'Hide camera feed'}
      >
        <Box
          onClick={() => onChange({ ...modes, videoOff: !modes.videoOff })}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.75,
            px: 1.75,
            py: 1,
            borderRadius: 999,
            cursor: 'pointer',
            color: modes.videoOff ? colors.textInverse : colors.textMuted,
            background: modes.videoOff ? colors.gradBrand : 'transparent',
            transition: 'all 180ms',
            fontFamily: '"Inter", sans-serif',
            fontSize: '0.75rem',
            fontWeight: 600,
            letterSpacing: '0.02em',
            '&:hover': {
              backgroundColor: modes.videoOff
                ? undefined
                : 'rgba(255,255,255,0.06)',
              color: modes.videoOff ? colors.textInverse : colors.text,
            },
          }}
        >
          {modes.videoOff ? (
            <VideocamOffOutlinedIcon sx={{ fontSize: 20 }} />
          ) : (
            <VideocamOutlinedIcon sx={{ fontSize: 20 }} />
          )}
          <Box component="span">Video</Box>
        </Box>
      </Tooltip>

      {/* Rotate camera — cycles through 0 → 90 → 180 → 270 → 0 */}
      <Tooltip title={`Rotate camera (${modes.rotation || 0}°)`}>
        <Box
          onClick={() =>
            onChange({ ...modes, rotation: ((modes.rotation || 0) + 90) % 360 })
          }
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.75,
            px: 1.5,
            py: 1,
            borderRadius: 999,
            cursor: 'pointer',
            color:
              modes.rotation && modes.rotation !== 0
                ? colors.textInverse
                : colors.textMuted,
            background:
              modes.rotation && modes.rotation !== 0
                ? colors.gradBrand
                : 'transparent',
            transition: 'all 180ms',
            fontFamily: '"Inter", sans-serif',
            fontSize: '0.75rem',
            fontWeight: 600,
            '&:hover': {
              backgroundColor:
                modes.rotation && modes.rotation !== 0
                  ? undefined
                  : 'rgba(255,255,255,0.06)',
              color:
                modes.rotation && modes.rotation !== 0
                  ? colors.textInverse
                  : colors.text,
            },
          }}
        >
          <ScreenRotationIcon
            sx={{
              fontSize: 20,
              transform: `rotate(${modes.rotation || 0}deg)`,
              transition: 'transform 300ms ease',
            }}
          />
        </Box>
      </Tooltip>
    </Box>
  );
}
